import { useRef, useState, type ReactNode } from 'react';
import { ChevronDown, FileText, Home, LogOut, MessageSquare, User, Building2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { MenuItem, MenuList } from '../../../ds/components';
import { useEscapeKey, useIsMobile, useOutsideClick } from '../../../ds/hooks';
import { LegalLink } from '../../reservations/public/PublicFrame';
import { portalLogout } from '../api';
import { ChatSheet } from '../questions/ChatSheet';
import { useNoIndex } from './PhoneFrame';
import { usePortal } from './PortalLayout';
import styles from './Portal.module.css';

const NAV = [
  { section: '', label: 'Übersicht', short: 'Übersicht', icon: Home },
  { section: 'objects', label: 'Meine Objekte', short: 'Objekte', icon: Building2 },
  { section: 'documents', label: 'Dokumente', short: 'Dokumente', icon: FileText },
  { section: 'questions', label: 'Fragen', short: 'Fragen', icon: MessageSquare },
] as const;

function ProfileMenu() {
  const { me, path } = usePortal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  useOutsideClick([anchor], () => setOpen(false), open);
  useEscapeKey(() => setOpen(false), open);

  async function logout() {
    setOpen(false);
    try {
      await portalLogout();
    } finally {
      queryClient.removeQueries({ queryKey: ['portal'] });
      void navigate(`/portal/${me.organization.slug}/login`, { replace: true });
    }
  }

  return (
    <div className={styles.profile} ref={anchor}>
      <button
        type="button"
        className={styles.profileButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.avatar} aria-hidden="true">
          {me.contact.initials}
        </span>
        <span className={styles.profileName}>{me.contact.fullName}</span>
        <ChevronDown size={15} strokeWidth={1.75} aria-hidden="true" className={styles.chevron} />
      </button>
      {open ? (
        <div className={styles.menu}>
          <MenuList label="Profil" onClose={() => setOpen(false)}>
            <MenuItem
              label="Mein Profil"
              icon={<User size={16} strokeWidth={1.75} aria-hidden="true" />}
              onSelect={() => {
                setOpen(false);
                void navigate(path('profile'));
              }}
            />
            <MenuItem
              label="Abmelden"
              icon={<LogOut size={16} strokeWidth={1.75} aria-hidden="true" />}
              onSelect={() => void logout()}
            />
          </MenuList>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Frame of the signed-in portal (Mandantenportal 14–22): the 72 px glass topbar with „DAMOKLES · Mandantenportal", the
 * navigation and the profile menu, the content on `--sand-100` and the lean footer with the organization's legal
 * links (PLAN §7). Below 768 px the navigation becomes a bottom tab bar and a floating „Fragen" button opens the chat
 * as a full-screen sheet.
 */
export function PortalFrame({ children }: { children: ReactNode }) {
  const { me, path, chatOpen, setChatOpen } = usePortal();
  const location = useLocation();
  const mobile = useIsMobile();
  useNoIndex('Mandantenportal');
  const onQuestions = location.pathname.startsWith(path('questions'));
  const legal = me.organization.legal;

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.brandBlock}>
          <div className={styles.brand}>DAMOKLES</div>
          <div className={styles.brandSub}>Mandantenportal</div>
        </div>
        <nav className={styles.nav} aria-label="Mandantenportal">
          {NAV.map((item) => (
            <NavLink
              key={item.section}
              to={path(item.section)}
              end={item.section === ''}
              className={({ isActive }) => clsx(styles.navLink, isActive && styles.navActive)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <ProfileMenu />
      </header>
      <main className={styles.main}>{children}</main>
      <footer className={styles.footer}>
        <span>{me.organization.name}</span>
        {legal.imprintUrl ? (
          <LegalLink url={legal.imprintUrl} title="Impressum" className={styles.legal} />
        ) : null}
        {legal.privacyUrl ? (
          <LegalLink url={legal.privacyUrl} title="Datenschutzhinweise" className={styles.legal} />
        ) : null}
      </footer>
      <nav className={styles.tabBar} aria-label="Mandantenportal mobil">
        {NAV.map((item) => (
          <NavLink
            key={item.section}
            to={path(item.section)}
            end={item.section === ''}
            className={({ isActive }) => clsx(styles.tab, isActive && styles.tabActive)}
          >
            <item.icon size={20} strokeWidth={1.5} aria-hidden="true" />
            <span>{item.short}</span>
          </NavLink>
        ))}
      </nav>
      {mobile && !chatOpen ? (
        <button
          type="button"
          className={clsx('pressable', styles.chatFab, onQuestions && styles.chatFabQuestions)}
          onClick={() => setChatOpen(true)}
        >
          <MessageSquare size={18} strokeWidth={1.5} aria-hidden="true" />
          Fragen
        </button>
      ) : null}
      {mobile ? <ChatSheet open={chatOpen} onClose={() => setChatOpen(false)} /> : null}
    </div>
  );
}
